import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, ArrowRight } from 'lucide-react';
export function ProblemSolution() {
  const problems = ['Tu web está desactualizada o directamente no tienes una', 'Pierdes clientes porque no te encuentran o no confían en lo que ven', 'No sabes cuántos clientes llegan gracias a tu presencia online', 'Tu imagen digital no refleja la calidad real de tu negocio'];
  const solutions = [{
    text: 'Web + carta digital + redes sociales trabajando juntos',
    metric: '+95% Eficiencia'
  }, {
    text: 'Diseño pensado para que cada visita se convierta en cliente',
    metric: '+120% Conversión'
  }, {
    text: 'Una base digital que crece contigo sin límites',
    metric: 'ROI Medible'
  }, {
    text: 'Imagen profesional que genera confianza al instante',
    metric: 'Top 1% Sector'
  }];
  return <section className="py-24 bg-[#050505] relative overflow-hidden" data-id="element-204">
      <div className="section-divider absolute top-0" data-id="element-205"></div>
      <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-206"></div>

      {/* Background Network Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20" data-id="element-207">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-208">
          <defs data-id="element-209">
            <pattern id="network-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-210">
              <circle cx="20" cy="20" r="1.5" fill="#10b981" opacity="0.5" data-id="element-211" />
              <circle cx="80" cy="40" r="1" fill="#10b981" opacity="0.3" data-id="element-212" />
              <circle cx="40" cy="80" r="2" fill="#10b981" opacity="0.4" data-id="element-213" />
              <path d="M20 20 L80 40 L40 80 Z" fill="none" stroke="#10b981" strokeWidth="0.5" opacity="0.2" data-id="element-214" />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#network-pattern)" data-id="element-215" />
        </svg>
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-216">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-217">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-218">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter" data-id="element-219">
              De la irrelevancia digital al{' '}
              <span className="text-gradient" data-id="element-220">liderazgo en tu sector</span>
            </h2>
            <p className="text-gray-400 text-xl font-medium" data-id="element-221">
              Las empresas estancadas operan con herramientas aisladas. Los
              líderes del mercado dominan a través de ecosistemas digitales
              integrados.
            </p>
          </motion.div>
        </div>

        <div className="max-w-5xl mx-auto relative" data-id="element-222">
          {/* Central Timeline Line (Desktop) / Left Line (Mobile) */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-223"></div>

          {/* Animated Progress Line */}
          <motion.div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-emerald/0 via-brand-emerald to-brand-emerald/0 -translate-x-1/2 origin-top" initial={{
          scaleY: 0
        }} whileInView={{
          scaleY: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-224"></motion.div>

          <div className="space-y-12 md:space-y-24" data-id="element-225">
            {problems.map((problem, index) => <div key={index} className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0" data-id="element-226">
                {/* Timeline Node */}
                <div className="absolute left-6 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10 flex items-center justify-center" data-id="element-227">
                  <motion.div initial={{
                scale: 0,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} viewport={{
                once: true,
                margin: '-100px'
              }} transition={{
                delay: 0.2,
                duration: 0.5,
                type: 'spring'
              }} className="relative" data-id="element-228">
                    <div className="absolute inset-0 bg-brand-emerald rounded-full animate-ping opacity-30" data-id="element-229"></div>
                    <div className="w-10 h-10 rounded-full bg-[#111] border-2 border-brand-emerald flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)] relative z-10" data-id="element-230">
                      <ArrowRight size={18} className="text-brand-emerald md:rotate-0 rotate-90" data-id="element-231" />
                    </div>
                  </motion.div>
                </div>

                {/* Problem (Left Side on Desktop) */}
                <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.6
            }} className="w-full md:w-1/2 pl-16 md:pl-0 md:pr-16" data-id="element-232">
                  <div className="bg-[#0a0a0a]/50 border border-red-500/10 rounded-2xl p-6 relative overflow-hidden group" data-id="element-233">
                    <div className="absolute inset-0 bg-grid-pattern-red opacity-5" data-id="element-234"></div>
                    <div className="flex items-start gap-4 relative z-10" data-id="element-235">
                      <div className="mt-1 w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 border border-red-500/20" data-id="element-236">
                        <X size={12} className="text-red-500/70" data-id="element-237" />
                      </div>
                      <p className="text-gray-500 font-medium text-lg leading-relaxed line-through decoration-red-500/30" data-id="element-238">
                        {problem}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Solution (Right Side on Desktop) */}
                <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="w-full md:w-1/2 pl-16 md:pl-16" data-id="element-239">
                  <div className="bg-gradient-to-br from-[#111] to-[#0a1a12] border border-brand-emerald/30 rounded-2xl p-6 relative overflow-hidden shadow-[0_0_30px_rgba(16,185,129,0.05)] hover:shadow-[0_0_40px_rgba(16,185,129,0.1)] transition-shadow duration-500" data-id="element-240">
                    <div className="absolute inset-0 bg-grid-pattern-emerald opacity-10" data-id="element-241"></div>
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-brand-emerald/10 blur-[40px] rounded-full" data-id="element-242"></div>

                    <div className="flex flex-col gap-4 relative z-10" data-id="element-243">
                      <div className="flex items-start gap-4" data-id="element-244">
                        <div className="mt-1 w-8 h-8 rounded-full bg-brand-emerald/20 flex items-center justify-center shrink-0 border border-brand-emerald/30 shadow-[0_0_10px_rgba(16,185,129,0.2)]" data-id="element-245">
                          <Check size={16} className="text-brand-emerald" data-id="element-246" />
                        </div>
                        <p className="text-white font-bold text-lg md:text-xl leading-relaxed" data-id="element-247">
                          {solutions[index].text}
                        </p>
                      </div>

                      <div className="pl-12" data-id="element-248">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-emerald/10 border border-brand-emerald/20 text-brand-emerald text-xs font-black uppercase tracking-widest" data-id="element-249">
                          {solutions[index].metric}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}